import React, { useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';

const ENTITY_TOKEN = /\[\[entity:([0-9a-fA-F-]{8,})\|([^\]]+)\]\]/g;

/** Entity ids referenced in AI text, in order of first appearance */
export function extractEntityIds(text: string): string[] {
  const ids: string[] = [];
  for (const match of Array.from(text.matchAll(ENTITY_TOKEN))) {
    if (!ids.includes(match[1])) ids.push(match[1]);
  }
  return ids;
}

/** Hides a half-streamed trailing token (e.g. "[[entity:12ab") until it completes */
function stripPartialToken(text: string): string {
  const open = text.lastIndexOf('[[');
  return open !== -1 && text.indexOf(']]', open) === -1 ? text.slice(0, open) : text;
}

interface IntelTextProps {
  text: string;
  /** Called when an entity chip is clicked */
  onEntity?: (id: string) => void;
  /** Hovering a chip, e.g. to highlight it on a graph */
  onEntityHover?: (id: string | null) => void;
  /** Colour of the chip dot per entity (e.g. entity kind colour) */
  entityColor?: (id: string) => string | undefined;
  streaming?: boolean;
  className?: string;
}

/**
 * Renders AI markdown with [[entity:<id>|<label>]] references as interactive chips.
 * Styled for calm reading: 13.5px body, quiet headings, hairline rules.
 */
export const IntelText: React.FC<IntelTextProps> = ({ text, onEntity, onEntityHover, entityColor, streaming, className = '' }) => {
  const markdown = useMemo(
    () =>
      stripPartialToken(text).replace(ENTITY_TOKEN, (_m, id: string, label: string) => `[${label.replace(/[[\]]/g, '')}](entity:${id})`),
    [text],
  );

  // Stable renderers: recreating them each render remounts every chip, which swallows clicks
  // that land while the parent re-renders (streamed text, hover state). Callbacks are read from a ref.
  const handlers = useRef({ onEntity, onEntityHover, entityColor });
  handlers.current = { onEntity, onEntityHover, entityColor };
  const components = useMemo<React.ComponentProps<typeof ReactMarkdown>['components']>(
    () => ({
      h1: ({ children }) => <h3 className="mb-1.5 mt-4 text-[15px] font-medium tracking-[-0.01em] text-ink first:mt-0">{children}</h3>,
      h2: ({ children }) => <h3 className="mb-1.5 mt-4 text-[14px] font-medium tracking-[-0.01em] text-ink first:mt-0">{children}</h3>,
      h3: ({ children }) => <h4 className="mb-1 mt-3 text-[13px] font-medium text-ink first:mt-0">{children}</h4>,
      p: ({ children }) => <p className="mb-2.5 last:mb-0">{children}</p>,
      ul: ({ children }) => <ul className="mb-2.5 list-disc space-y-1 pl-4 marker:text-neutral-300">{children}</ul>,
      ol: ({ children }) => <ol className="mb-2.5 list-decimal space-y-1 pl-4 marker:text-neutral-400">{children}</ol>,
      strong: ({ children }) => <strong className="font-medium text-ink">{children}</strong>,
      hr: () => <hr className="my-3 border-neutral-200/70" />,
      blockquote: ({ children }) => (
        <blockquote className="my-2.5 border-l-2 border-neutral-200 pl-3 text-neutral-500">{children}</blockquote>
      ),
      code: ({ children }) => <code className="rounded bg-neutral-100 px-1 py-0.5 font-ui-mono text-[12px]">{children}</code>,
      a: ({ href, children }) => {
        if (href?.startsWith('entity:')) {
          const id = href.slice('entity:'.length);
          const color = handlers.current.entityColor?.(id);
          return (
            <button
              type="button"
              onClick={() => handlers.current.onEntity?.(id)}
              onMouseEnter={() => handlers.current.onEntityHover?.(id)}
              onMouseLeave={() => handlers.current.onEntityHover?.(null)}
              onFocus={() => handlers.current.onEntityHover?.(id)}
              onBlur={() => handlers.current.onEntityHover?.(null)}
              className="mx-0.5 inline-flex items-baseline gap-1 rounded-md bg-neutral-100 px-1.5 text-[0.95em] font-medium text-ink transition-colors hover:bg-neutral-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
            >
              {color && <span className="relative -top-px inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />}
              {children}
            </button>
          );
        }
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary-600 underline decoration-primary-600/30 underline-offset-2 hover:decoration-primary-600">
            {children}
          </a>
        );
      },
    }),
    [],
  );

  return (
    <div className={`intel-text text-[13.5px] leading-[1.65] text-neutral-700 ${className}`}>
      <ReactMarkdown urlTransform={(url) => url} components={components}>
        {markdown}
      </ReactMarkdown>
      {streaming && <span className="ml-0.5 inline-block h-3.5 w-1.5 translate-y-0.5 animate-pulse rounded-sm bg-neutral-300" aria-hidden="true" />}
    </div>
  );
};
